import {
  ArrowRight,
  AudioLines,
  Bookmark,
  Check,
  Cloud,
  Ear,
  FolderHeart,
  HardDrive,
  Headphones,
  Library,
  NotebookPen,
  Pause,
  Rss,
} from "lucide-react";
import { Link, type MetaFunction } from "react-router";

import { Button } from "@/ui/button";

import { pausedFeatures } from "../features";
import { MemoryPreview } from "./memory-preview";
import { TrackingPreview } from "./tracking-preview";

export const meta: MetaFunction = () => [
  { title: publicEnv.siteTitle },
  {
    name: "description",
    content: pausedFeatures.youtube
      ? "Pause on the exact moments worth keeping, add a title and note, and return to them across RSS podcasts and YouTube conversations."
      : "Pause on the exact moments worth keeping, add a title and note, and return to them across RSS podcasts.",
  },
];

// Explain Paused as a memory practice, not another consumption tracker.
export default function Marketing() {
  const startLabel =
    publicEnv.stripeTrialDays > 0
      ? `Start your free ${publicEnv.stripeTrialDays}-day trial`
      : "Open your archive";

  return (
    <>
      <section className="grid items-center gap-10 pt-14 pb-20 sm:pt-20 sm:pb-28 lg:grid-cols-[0.94fr_1.06fr] lg:gap-16 lg:pt-24 lg:pb-32">
        <div>
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            Notes that keep their place
          </p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl leading-[0.96] font-semibold tracking-[-0.045em] sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
            Some thoughts are
            <br />
            worth pausing for.
          </h1>
          <p className="text-muted-foreground mt-7 max-w-xl text-lg leading-8">
            Pause on the exact moment an idea finds you. Keep the timestamp with
            your own words, then return to it without searching through the
            whole episode again.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="h-12 px-6 text-base" size="lg">
              <Link to="/home">
                {startLabel}
                <ArrowRight />
              </Link>
            </Button>
            <Button
              asChild
              className="h-12 px-6 text-base"
              size="lg"
              variant="outline"
            >
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>

          <ul className="text-muted-foreground mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <li className="flex items-center gap-1.5">
              <Check className="text-primary size-4" /> Exact timestamps
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="text-primary size-4" />{" "}
              {pausedFeatures.youtube ? "RSS and YouTube" : "RSS podcasts"}
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="text-primary size-4" /> Private notes
            </li>
          </ul>
        </div>

        <MemoryPreview />
      </section>

      <section className="source-strip grid overflow-hidden rounded-2xl border sm:grid-cols-2 lg:grid-cols-4">
        <SourcePoint
          icon={Headphones}
          label={pausedFeatures.youtube ? "Audio and video" : "Podcast audio"}
        >
          {pausedFeatures.youtube
            ? "Save moments from RSS audio and YouTube videos."
            : "Save moments from RSS podcasts."}
        </SourcePoint>
        <SourcePoint icon={Rss} label="RSS feeds">
          Keep podcast metadata and new episodes refreshable.
        </SourcePoint>
        <SourcePoint icon={Bookmark} label="Precise pauses">
          Keep the timestamp that made you pause.
        </SourcePoint>
        <SourcePoint icon={FolderHeart} label="Curated threads">
          Group full episodes and exact excerpts in your own order.
        </SourcePoint>
      </section>

      <section
        className="grid gap-12 py-24 sm:py-32 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20"
        id="why"
      >
        <div>
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            Listening is not collecting
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold tracking-[-0.03em] sm:text-5xl">
            Your queue knows what you started. Paused remembers what made you
            stop.
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <article className="bg-muted/65 rounded-2xl border border-dashed p-6 sm:p-8">
            <span className="text-muted-foreground font-mono text-[10px] tracking-[0.14em] uppercase">
              The usual podcast app
            </span>
            <p className="text-muted-foreground mt-8 font-serif text-3xl leading-tight line-through decoration-1">
              247 unplayed episodes
            </p>
            <p className="text-muted-foreground mt-4 leading-7">
              A growing measure of everything you have not finished.
            </p>
          </article>
          <article className="archive-card rounded-2xl border p-6 sm:p-8">
            <span className="text-primary font-mono text-[10px] font-semibold tracking-[0.14em] uppercase">
              Paused FYI
            </span>
            <p className="mt-8 font-serif text-3xl leading-tight font-semibold">
              38 moments you chose to keep
            </p>
            <p className="text-muted-foreground mt-4 leading-7">
              A small, meaningful record of how your thinking has grown.
            </p>
          </article>
        </div>
      </section>

      <section className="border-y py-24 sm:py-32" id="how-it-works">
        <div className="max-w-3xl">
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            A three-part listening ritual
          </p>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-[-0.03em] sm:text-5xl">
            Hear it. Pause it. Keep it.
          </h2>
        </div>

        <div className="bg-border mt-12 grid gap-px overflow-hidden rounded-2xl border lg:grid-cols-3">
          <StepCard icon={Ear} number="01" title="Play naturally">
            {pausedFeatures.youtube
              ? "Play an RSS episode or a YouTube video."
              : "Play an RSS podcast episode."}{" "}
            The original provider serves the media while Paused keeps your
            place.
          </StepCard>
          <StepCard icon={Pause} number="02" title="Pause on the thought">
            Pause where something deserves your attention. Keep that timestamp
            with a memorable title and your own note.
          </StepCard>
          <StepCard icon={Library} number="03" title="Return with context">
            Browse moments as cards or scan them as a table. Search your ideas
            instead of hunting through episode feeds.
          </StepCard>
        </div>
      </section>

      <section className="grid items-center gap-12 border-b py-24 sm:py-32 lg:grid-cols-[0.68fr_1.32fr] lg:gap-16">
        <div>
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            Structure when it helps
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold tracking-[-0.03em] sm:text-5xl">
            Every Podcast opens as a clean source.
          </h2>
          <p className="text-muted-foreground mt-6 leading-7">
            Its complete public feed is one playable table with your private
            Episode notes, status, ratings, revisit flags, and Collection
            actions layered on top. The first note creates a reusable
            full-episode Moment; the Podcast and Episode metadata stays clean.
          </p>
          <Button asChild className="mt-7" variant="outline">
            <Link to="/add">
              Find your first episode <ArrowRight />
            </Link>
          </Button>
        </div>
        <TrackingPreview />
      </section>

      <section className="grid gap-12 py-24 sm:py-32 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div>
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            Private continuity, clear pricing
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold tracking-[-0.03em] sm:text-5xl">
            One verified account. Your archive everywhere.
          </h2>
          <p className="text-muted-foreground mt-6 max-w-xl leading-7">
            Email magic links or Google sign-in protect the private notes and
            playback history that follow you across browsers. One subscription
            opens the complete archive, and no payment method is required to
            start an offered free trial.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/pricing">
                See pricing <ArrowRight />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/login">Sign in</Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <article className="paper-card flex min-h-72 flex-col rounded-2xl p-6 sm:p-8">
            <HardDrive className="text-primary size-6" />
            <div className="mt-auto pt-12">
              <p className="text-muted-foreground font-mono text-[10px] tracking-[0.12em] uppercase">
                Passwordless access
              </p>
              <h3 className="mt-3 font-serif text-2xl font-semibold">
                Verify, then get out of the way
              </h3>
              <p className="text-muted-foreground mt-3 leading-7">
                Use a short-lived email link, verification code, or Google.
                There is no password database to manage.
              </p>
            </div>
          </article>
          <article className="archive-card flex min-h-72 flex-col rounded-2xl border p-6 sm:p-8">
            <Cloud className="text-primary size-6" />
            <div className="mt-auto pt-12">
              <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.12em] uppercase">
                Durable archive
              </p>
              <h3 className="mt-3 font-serif text-2xl font-semibold">
                Your context travels with you
              </h3>
              <p className="text-muted-foreground mt-3 leading-7">
                Referenced Podcasts, Collections, Moments, progress, and your
                Collection or Moment fields can be exported as JSON at any time.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section
        className="grid items-center gap-14 border-t py-24 sm:py-32 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20"
        id="sources"
      >
        <div className="source-orbit relative mx-auto grid aspect-square w-full max-w-md place-items-center rounded-full border">
          <div className="bg-card grid size-32 place-items-center rounded-full border shadow-xl">
            <AudioLines className="text-primary size-10" />
          </div>
          <span className="bg-card absolute top-[8%] left-[20%] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-lg">
            <Headphones className="text-primary size-4" /> RSS audio
          </span>
          <span className="bg-card absolute top-[36%] right-[-2%] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-lg">
            <Rss className="text-primary size-4" /> Open feeds
          </span>
          <span className="bg-card absolute right-[13%] bottom-[10%] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-lg">
            <NotebookPen className="text-primary size-4" /> Your notes
          </span>
          <span className="bg-card absolute bottom-[18%] left-[2%] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-lg">
            <Bookmark className="text-primary size-4" /> Moments
          </span>
        </div>

        <div>
          <p className="text-primary font-mono text-[10px] font-semibold tracking-[0.18em] uppercase">
            One listening archive
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold tracking-[-0.03em] sm:text-5xl">
            Conversations, with the context intact.
          </h2>
          <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
            {pausedFeatures.youtube
              ? "Open an RSS podcast or paste a YouTube video link."
              : "Open an RSS podcast or paste a feed link."}{" "}
            Spend time with a conversation and keep the ideas that matter, with
            your notes and collections in one place.
          </p>
          <ul className="mt-7 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Check className="text-primary mt-0.5 size-4 shrink-0" />
              Podcast Index helps you find the RSS podcasts you already know.
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-primary mt-0.5 size-4 shrink-0" />
              {pausedFeatures.youtube
                ? "RSS feeds and YouTube links keep each recording connected to its source."
                : "RSS feeds keep each recording connected to its source."}
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-primary mt-0.5 size-4 shrink-0" />
              Every Moment keeps a full-episode or exact excerpt range and its
              source link.
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-primary mt-0.5 size-4 shrink-0" />
              Collections can mix whole Episodes and exact Moments from any
              Podcast and publish a live page at their canonical URL.
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-primary mt-0.5 size-4 shrink-0" /> The
              Source index includes only Podcasts and Episodes referenced by a
              Moment or Collection.
            </li>
          </ul>
        </div>
      </section>

      <section className="cta-field mb-24 overflow-hidden rounded-3xl px-6 py-16 text-center text-white sm:px-12 sm:py-20">
        <Pause className="mx-auto size-7 opacity-75" />
        <p className="mt-6 font-mono text-[10px] tracking-[0.18em] uppercase opacity-70">
          A quieter way to listen
        </p>
        <h2 className="mx-auto mt-4 max-w-3xl font-serif text-4xl leading-tight font-semibold tracking-[-0.03em] sm:text-6xl">
          Pause the episode. Keep the thought.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-white/70">
          Build a private archive of the moments that made listening matter.
        </p>
        <Button
          asChild
          className="mt-8 h-12 bg-white px-6 text-base text-black hover:bg-white/90"
          size="lg"
        >
          <Link to="/home">
            {startLabel} <ArrowRight />
          </Link>
        </Button>
      </section>
    </>
  );
}

interface SourcePointProps {
  children: string;
  icon: typeof Headphones;
  label: string;
}

function SourcePoint({ children, icon: Icon, label }: SourcePointProps) {
  return (
    <article className="bg-card flex gap-3 p-5 sm:p-6">
      <Icon className="text-primary mt-0.5 size-5 shrink-0" />
      <div>
        <h2 className="font-medium">{label}</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-6">
          {children}
        </p>
      </div>
    </article>
  );
}

interface StepCardProps {
  children: string;
  icon: typeof Ear;
  number: string;
  title: string;
}

function StepCard({ children, icon: Icon, number, title }: StepCardProps) {
  return (
    <article className="bg-card flex min-h-80 flex-col p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground font-mono text-[10px] tracking-[0.12em] uppercase">
          {number}
        </span>
        <Icon className="text-primary size-5" />
      </div>
      <div className="mt-auto pt-12">
        <h3 className="font-serif text-2xl font-semibold">{title}</h3>
        <p className="text-muted-foreground mt-3 leading-7">{children}</p>
      </div>
    </article>
  );
}
